/**
 * Typed application config.
 *
 * The ONE place `import.meta.env` is read. Everything else imports `env` from
 * here, so:
 *   - env access is typed and validated (a missing/blank required var fails
 *     loudly at startup, not as a confusing 404 later),
 *   - defaults live in one spot,
 *   - feature flags are booleans, not string comparisons scattered around.
 *
 * Add a new var: declare it in vite-env.d.ts, read it here, expose it on `env`.
 * Never read `import.meta.env.VITE_*` anywhere else.
 */

type Mode = "development" | "production" | "test";

function str(value: string | undefined, fallback: string): string {
  const v = value?.trim();
  return v === undefined || v === "" ? fallback : v;
}

function bool(value: string | undefined, fallback: boolean): boolean {
  if (value === undefined || value.trim() === "") return fallback;
  return value.trim().toLowerCase() === "true" || value.trim() === "1";
}

const mode = (import.meta.env.MODE ?? "development") as Mode;

export const env = {
  mode,
  isDev: mode === "development",
  isProd: mode === "production",

  /** API base. Defaults to the dev proxy path so a bare checkout just works. */
  apiBase: str(import.meta.env.VITE_API_BASE, "/api/v1"),

  /** Empty string disables the reCAPTCHA step (auth reads this). */
  recaptchaSiteKey: str(import.meta.env.VITE_RECAPTCHA_SITE_KEY, ""),

  /**
   * Feature flags. Default OFF in production, so a half-built feature can't
   * ship by accident; flip per-env in the relevant .env file.
   */
  flags: {
    /** Live updates via WebSocket/SSE (module handoff §7 Option B). */
    realtimeModules: bool(import.meta.env.VITE_FLAG_REALTIME_MODULES, false),
    /** Show the query devtools panel. */
    queryDevtools: bool(import.meta.env.VITE_FLAG_QUERY_DEVTOOLS, mode === "development"),
  },
} as const;

export type Env = typeof env;
